import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatMenuTrigger} from '@angular/material/menu';
import {FloatLabelType, MatFormFieldAppearance} from '@angular/material/form-field';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';

export interface DateRange {
    from: Date | null;
    to: Date | null;
}

/** Кнопка швидкого вибору періоду в меню */
export interface PeriodOption {
    /** Ключ перекладу назви */
    label: string;
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
    disabled: boolean;
}

/** Текст поля: або готовий рядок, або ключ перекладу з параметрами */
interface PeriodDisplay {
    text?: string;
    key?: string;
    params?: Record<string, number>;
}

/** Дата в будь-якому вигляді, який розуміє dayjs */
type DateInput = Date | string | dayjs.Dayjs | null | undefined;

/** Найменший рік, до якого можна гортати меню, якщо minDate не задано */
const ABSOLUTE_MIN_YEAR = 2000;

/**
 * Вибір періоду через меню: місяці вибраного року, а з showLongPeriods — ще квартали, півріччя, 9 місяців і рік.
 * displayType 'input' — поле форми (ControlValueAccessor зі значенням DateRange),
 * 'iconButton' — лише кнопка з іконкою, що відкриває меню (наприклад, поруч із полями «Від/До»).
 * valueType 'month' — значення форми це Date першого дня вибраного місяця (як у колишнього app-months-datepicker),
 * 'range' — об'єкт DateRange.
 */
@Component({
    selector: 'range-date-picker',
    templateUrl: './range-date-picker.component.html',
    styleUrls: ['./range-date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => RangeDatePickerComponent),
        multi: true,
    }],
})
export class RangeDatePickerComponent implements ControlValueAccessor, OnChanges {
    @Input() displayType: 'input' | 'iconButton' = 'input';
    /** Тип значення форми: DateRange або Date першого дня місяця */
    @Input() valueType: 'range' | 'month' = 'range';
    @Input() label = '';
    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() floatLabel: FloatLabelType = 'always';
    @Input() fieldClass = 'no-padding full-width';
    @Input() clearable = false;
    /** Показувати квартали, півріччя, 9 місяців і рік; без нього в меню лише місяці */
    @Input() showLongPeriods = false;
    @Input() disabled = false;
    @Input() minDate: DateInput = null;
    @Input() maxDate: DateInput = null;
    @Input() from: Date | null | undefined = null;
    @Input() to: Date | null | undefined = null;

    @Output() rangeChange = new EventEmitter<DateRange>();

    @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

    /** Рік, що показаний у меню */
    year = dayjs().year();
    months: PeriodOption[] = [];
    longPeriods: PeriodOption[] = [];
    display: PeriodDisplay | null = null;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private onChange = (_: DateRange | Date | null) => {};
    private onTouched = () => {};

    constructor(private cdr: ChangeDetectorRef) {
        this.buildPeriods();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['minDate'] || changes['maxDate']) {
            this.buildPeriods();
        }
        if (changes['from'] || changes['to']) {
            this.updateDisplay();
        }
    }

    get hasValue(): boolean {
        return !!this.from && !!this.to;
    }

    get canPrevYear(): boolean {
        const minYear = this.minDate ? dayjs(this.minDate).year() : ABSOLUTE_MIN_YEAR;
        return this.year > minYear;
    }

    get canNextYear(): boolean {
        return !this.maxDate || this.year < dayjs(this.maxDate).year();
    }

    openMenu() {
        if (!this.disabled) {
            this.menuTrigger?.openMenu();
        }
    }

    /** При відкритті меню показуємо рік вибраного періоду (або поточний) */
    onMenuOpened() {
        const year = dayjs(this.from ?? this.to ?? undefined).year();
        if (year !== this.year) {
            this.year = year;
            this.buildPeriods();
        }
    }

    onMenuClosed() {
        this.onTouched();
    }

    changeYear(step: number) {
        if (step < 0 ? !this.canPrevYear : !this.canNextYear) {
            return;
        }

        this.year += step;
        this.buildPeriods();
    }

    isSelected(period: PeriodOption): boolean {
        return this.hasValue
            && period.from.isSame(this.from, 'day')
            && period.to.isSame(this.to, 'day');
    }

    selectPeriod(period: PeriodOption) {
        if (period.disabled) {
            return;
        }

        this.setRange(period.from.toDate(), period.to.toDate());
        this.menuTrigger?.closeMenu();
    }

    clear() {
        this.setRange(null, null);
    }

    // <editor-fold desc="ControlValueAccessor">
    writeValue(value: DateRange | DateInput): void {
        if (this.valueType === 'month') {
            // Форми передають сюди Date, dayjs або рядок — беремо місяць цієї дати
            const date = value ? dayjs(value as DateInput) : null;
            this.from = date?.isValid() ? date.startOf('month').toDate() : null;
            this.to = date?.isValid() ? date.endOf('month').toDate() : null;
        } else {
            const range = value as DateRange | null;
            this.from = range?.from ?? null;
            this.to = range?.to ?? null;
        }
        this.updateDisplay();
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: DateRange | Date | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }
    // </editor-fold>

    private setRange(from: Date | null, to: Date | null) {
        this.from = from;
        this.to = to;
        this.updateDisplay();
        const range = {from, to};
        if (this.valueType === 'month') {
            // Так само, як app-months-datepicker: перше число місяця, 00:00 за місцевим часом
            this.onChange(from ? new Date(from.getFullYear(), from.getMonth(), 1) : null);
        } else {
            this.onChange(from && to ? range : null);
        }
        this.rangeChange.emit(range);
    }

    /** Назва періоду для поля: місяць, квартал, півріччя, 9 місяців, рік або «дд.мм.рррр – дд.мм.рррр» */
    private updateDisplay() {
        if (!this.from || !this.to) {
            this.display = null;
            return;
        }

        const from = dayjs(this.from).startOf('day');
        const to = dayjs(this.to);
        const year = from.year();
        const wholeMonths = from.date() === 1 && to.isSame(to.endOf('month'), 'day') && to.year() === year;
        const span = to.month() - from.month() + 1;

        if (wholeMonths && span === 1) {
            const text = from.locale('uk').format('MMMM YYYY');
            this.display = {text: text.charAt(0).toUpperCase() + text.slice(1)};
        } else if (wholeMonths && span === 3 && from.month() % 3 === 0) {
            this.display = {key: 'common.periodPicker.display.quarter', params: {index: from.month() / 3 + 1, year}};
        } else if (wholeMonths && span === 6 && from.month() % 6 === 0) {
            this.display = {key: 'common.periodPicker.display.halfYear', params: {index: from.month() / 6 + 1, year}};
        } else if (wholeMonths && span === 9 && from.month() === 0) {
            this.display = {key: 'common.periodPicker.display.nineMonths', params: {year}};
        } else if (wholeMonths && span === 12) {
            this.display = {key: 'common.periodPicker.display.year', params: {year}};
        } else {
            this.display = {text: `${from.format('DD.MM.YYYY')} – ${to.format('DD.MM.YYYY')}`};
        }
    }

    /** Перераховує кнопки меню для показаного року з урахуванням minDate/maxDate */
    private buildPeriods() {
        const yearStart = dayjs().year(this.year).startOf('year');
        const today = dayjs();
        // «цей квартал» / «це півріччя» беруться для поточного місяця, але в показаному році
        const quarterStart = yearStart.month(Math.floor(today.month() / 3) * 3);
        const halfStart = yearStart.month(today.month() < 6 ? 0 : 6);

        this.months = Array.from({length: 12}, (_, i) => this.period(
            `common.periodPicker.months.${i + 1}`,
            yearStart.month(i),
            yearStart.month(i).endOf('month'),
        ));

        // Порядок відповідає сітці у два стовпці: ліворуч квартали, праворуч довші періоди
        this.longPeriods = [
            this.quarter(1),
            this.period('common.periodPicker.currentQuarter', quarterStart, quarterStart.add(2, 'month').endOf('month')),
            this.quarter(2),
            this.period('common.periodPicker.currentHalfYear', halfStart, halfStart.add(5, 'month').endOf('month')),
            this.quarter(3),
            this.period('common.periodPicker.nineMonths', yearStart, yearStart.month(8).endOf('month')),
            this.quarter(4),
            this.period('common.periodPicker.year', yearStart, yearStart.endOf('year')),
        ];
    }

    private quarter(index: number): PeriodOption {
        const start = dayjs().year(this.year).startOf('year').month((index - 1) * 3);
        return this.period(`common.periodPicker.quarter${index}`, start, start.add(2, 'month').endOf('month'));
    }

    private period(label: string, from: dayjs.Dayjs, to: dayjs.Dayjs): PeriodOption {
        const disabled = (!!this.minDate && to.isBefore(this.minDate, 'day'))
            || (!!this.maxDate && from.isAfter(this.maxDate, 'day'));
        return {label, from: from.startOf('day'), to: to.endOf('day'), disabled};
    }
}
